import styled from '@emotion/styled';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Accordion } from 'semantic-ui-react';

import { useTranslation } from 'react-i18next';
import {
    getCompanyAdditionalBenefits,
    getUserInformation,
} from 'src/modules/user/selectors/userSelectors';
import icon from 'src/public/img/arrow_down.svg';
import { Button } from 'src/shared/components/Button';
import { Modal } from 'src/shared/components/Modal';
import { color, font, layout, shadow } from 'src/shared/styles/variables';

const StyledModal = styled(Modal)({
    width: '100% !important',

    '& .ant-modal-content': {
        maxWidth: layout.modalWidth.smallPlus,
        margin: '0 auto',
        borderRadius: 7,

        '& .ant-modal-body': {
            padding: '2.7rem',
        },
    },
});

const HeadingText = styled.h3(
    {
        color: color.pickledBluewood,
        marginBottom: 6,
    },
    font.black(font.size.almostExtraLarge),
);

const DescriptionText = styled.p(
    {
        color: color.blueBayoux,
        marginBottom: 6,
    },
    font.roman(font.size.base),
);

const ContentWrapper = styled.div({
    maxWidth: 490,
    margin: '0 auto',
});

const BenefitWrapper = styled.div({
    display: 'flex',
    padding: '1rem 1.3rem',
    flexDirection: 'column',
    marginTop: '1rem',
    borderRadius: 5,
    boxShadow: shadow.lightCardShadow,
    cursor: 'pointer',
});

const BenefitContentWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const BenefitName = styled.p({
    color: color.ebonyClay,
    textTransform: 'capitalize',
    ...font.black(font.size.medium),
});

const BenefitDescription = styled.p({
    color: color.blueBayoux,
    ...font.roman(font.size.base),
});

export enum AdditionalBenefitsModalTestID {
    Wrapper = 'additional_benefits_modal_wrapper',
    BenefitActive = 'additional_benefits_modal_benefit_active',
    BenefitNotActive = 'additional_benefits_modal_benefit_not_active',
    BenefitButton = 'additional_benefits_modal_benefit_button',
}

interface Props {
    visible: boolean;
    onToggle: () => void;
}

export const AdditionalBenefitsModal: React.FC<Props> = ({ onToggle, visible }) => {
    const { t } = useTranslation();
    const [activeBenefit, setActiveBenefit] = useState('');

    const { company } = useSelector(getUserInformation);
    const companyAdditionalBenefits = useSelector(getCompanyAdditionalBenefits);

    return (
        <StyledModal visible={visible} onCancel={onToggle} footer={null} width="auto" centered>
            <HeadingText>{t('user:additional_benefits.modal.title')}</HeadingText>
            <DescriptionText>
                {t('user:additional_benefits.modal.description', { company })}
            </DescriptionText>

            <ContentWrapper data-testid={visible && AdditionalBenefitsModalTestID.Wrapper}>
                {companyAdditionalBenefits.map(benefit => {
                    const isActive = benefit.id === activeBenefit;

                    return (
                        <BenefitWrapper
                            key={benefit.id}
                            data-testid={
                                isActive
                                    ? AdditionalBenefitsModalTestID.BenefitActive
                                    : AdditionalBenefitsModalTestID.BenefitNotActive
                            }
                            onClick={() =>
                                setActiveBenefit(active =>
                                    active === benefit.id ? '' : benefit.id,
                                )
                            }
                        >
                            <Accordion>
                                <Accordion.Title active={isActive}>
                                    <BenefitContentWrapper>
                                        <BenefitName>{benefit.title}</BenefitName>
                                        <img
                                            src={icon}
                                            style={{
                                                transform: isActive ? 'rotateX(180deg)' : '',
                                            }}
                                        />
                                    </BenefitContentWrapper>
                                </Accordion.Title>
                                <Accordion.Content active={isActive}>
                                    <BenefitDescription>{benefit.description}</BenefitDescription>

                                    <Button
                                        testID={AdditionalBenefitsModalTestID.BenefitButton}
                                        onClick={() => window.open(benefit.link, '_blank')}
                                        type="accept"
                                        marginTop={15}
                                        small
                                        fullWidth
                                    >
                                        {t('user:additional_benefits.modal.go_to_website')}
                                    </Button>
                                </Accordion.Content>
                            </Accordion>
                        </BenefitWrapper>
                    );
                })}
            </ContentWrapper>
        </StyledModal>
    );
};
